import dayjs from 'dayjs';
import { Http } from '@/lib/http';

export class UniversaIntegrationService extends Http {
  constructor() {
    super();
    this.endPoint = 'integration/core';
  }

  /**
   * checkStatus checks if universa core endpoints are online
   * @throws HttpError
   */
  async checkStatus() {
    try {
      return this.httpClient().post(`${this.endPoint}`, {
        action: 'checkStatus'
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * searchPerson searches for a person from core api based on the params provide
   * NOTE: At least one property must provided
   * @param {account_number: string, tax_number: string, first_name: string, dob: string, limit: number} searchParams
   * @return Promise<any>
   */
  async searchPerson({ firstName, lastName, limit }) {
    return this.httpClient().post(`${this.endPoint}`, {
      body: {
        action: 'personSearch',
        params: {
          fName: firstName,
          lName: lastName,
          limit
        }
      }
    });
  }

  async createLoan(loan) {
    try {
      const guarantorTypes = ['guarantor', 'co-borrower', 'co-maker'];

      const loanAmount =
        this.loanManagementService.calculateTotalLoanAmount(loan);

      const firstPaymentDate = dayjs()
        .add(loan.frequencyFirstPaymentDays || 30, 'day')
        .format('YYYY-MM-DD');

      //Get the first borrower core id
      const personCoreId = loan.loanDistribution.filter(
        (el) =>
          el.distributionType === 'internal' && el.personType == 'borrower'
      )[0].coreId;

      let loanCreationParams = {
        firstPayDate: firstPaymentDate,
        loanRef: loan.id,
        accountNum: loan.account.mainAccountNumber,
        loanProductCode: loan.loanProductDetails.loanProductCode,
        loanType:
          loan.loanProductDetails.loanTypeDetails.loanTypeCategory.toUpperCase(), // Acceptable values ["REAL ESTATE","MOTOR VEHICLE","CASH SECURED","UNSECURED"]
        ownerCoreId: personCoreId, //person on account core id
        loanOfficerId: loan.processingOfficer.employmentCoreId.toString(), // Get this from mappings, loans officer
        approvingOfficerId: loan.approvingOfficer.employmentCoreId.toString(),
        creator: loan.disbursementOfficer.employmentCoreId.toString(), // ID of person who is disbursing loan

        startDate: dayjs(loan.dateCreated).format('YYYY-MM-DD'), //YYYY-MM-DD
        maturityDate: dayjs(loan.maturityDate).format('YYYY-MM-DD'),
        originalAmount: Number(loanAmount).toFixed(2), //#.##
        installmentAmt: Number(loan.frequencyPayment).toFixed(2), //#.##
        installmentFreq: loan.frequencyName, // See below for acceptable values
        intRate: Number(loan.interest).toFixed(2), //#.##
        branchCode: loan.accreditedBranch.branchCode,

        persons: loan.loanDistribution
          ?.filter((el) => guarantorTypes.includes(el.personType))
          .map(({ person }) => {
            const p = {
              coreId: person.coreId,
              firstName: person.firstName,
              middleName: person.middleName || '-',
              lastName: person.lastName,
              relationship: person.personType // Acceptable values [borrower, guarantor, co-borrower, co-maker]
            };

            return p;
          })
      };

      if (loan.loanCollaterals && loan.loanCollaterals.length > 0) {
        loanCreationParams.collaterals = loan.loanCollaterals.map((el) => {
          let col = {
            type: el.collateral?.collateralType,
            description:
              el.collateral?.description || 'no description provided',
            value: Number(el.pledgedAmount || 0).toFixed(2) //#.##
            // existingPledgeAmt: , //#.## Optional
            // existingPledgeDesc: "None", // Required if existingPledgeAmt is set
          };

          if (el.dateOfValuation || false) {
            col['valuationDate'] = el.dateOfValuation; // Optional
          }
          return col;
        });
      }

      let action = 'createLoan';

      if (loan.type === 'top-up') {
        action = 'topupLoan';
        loanCreationParams.existingLoanRef =
          (loan.loansConsolidated[0] || {}).bank_loan_id || null;
      }

      return await this.httpClient().post(`${this.endPoint}`, {
        action,
        params: loanCreationParams
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * getPersonDetails returns all loans disbursed to a person
   * @param {string} coreId
   * @return Promise<any>
   */
  getPersonLoans(coreId) {
    return this.httpClient().post(`${this.endPoint}`, {
      action: 'getPersonLoans',
      params: {
        coreId
      }
    });
  }

  /**
   * getPersonDetails returns person's general information from core api if available
   * @param {string} coreId
   * @return Promise<any>
   */
  getPersonDetails(coreId) {
    return this.httpClient().post(`${this.endPoint}`, {
      action: 'getPersonDetails',
      params: {
        coreId
      }
    });
  }

  /**
   * findAndUpdatePersonRecordLocally searches the core and update person details
   * @param {*} record
   * @returns
   */
  async findAndUpdatePersonRecord(person) {
    try {
      //retrieve person details from core
      const { data } = await this.getPersonDetails(person.coreId);
      let record = data.data || data;

      console.log('findANd Up: ', record);
      //exit if not found
      if (!record) return null;

      //format record according to how it is stored locally
      const formattedRecord = UniversaIntegrationService.formatPersonRecord(
        record.person || {}
      );

      //function to compare records and update accordingly
      const updateRecord = (obj, updaterObj) => {
        for (const key in updaterObj) {
          if (!obj[key]) {
            obj[key] = updaterObj[key];
            continue;
          }

          if (typeof updaterObj[key] === 'object') {
            updateRecord(obj[key], updaterObj[key]);
            continue;
          }

          if (Array.isArray(updaterObj[key])) {
            updaterObj[key].forEach((el, i) => {
              if (!obj[key][i]) {
                obj[key].push(el);
              } else if (typeof el === 'object') {
                updateRecord(obj[key][i], el);
              } else {
                if (el !== obj[key][i]) {
                  objkey[i] = el;
                }
              }
            });
          }

          if (updaterObj[key] !== obj[key] && updaterObj[key]) {
            obj[key] = updaterObj[key];
          }
        }
      };

      updateRecord(person, formattedRecord.persons?.[0] || {});

      return UniversaIntegrationService.formatAccounts(
        person.coreId,
        record?.person?.accounts || []
      );
    } catch (e) {
      throw e;
    }
  }

  /**
   * return complete person object from core record
   * @param {object | array} record
   * @return Object<any>
   */
  static formatPersonRecord(record) {
    //? Query about account branch ID
    let person = {};
    person.coreId = record.core_id || null;
    person.personClass = 'primary';
    person.foundOn = record.foundOn;
    person.existLocally = record.exist_locally;
    person.title = record.title || null;
    person.firstName = record.first_name || '';
    person.middleName = record.middle_name || '';
    person.lastName = record.last_name || '';
    person.gender = record.gender || '';
    person.dateOfBirth = record.date_of_birth || null;
    person.trn = record.trn || null;
    person.nearestContact = {};
    person.livingAddress = {};
    person.mailingAddress = {};
    person.contact = [];
    person.employer = [];
    person.identifications = [];

    person.emailAddress = record.email;
    if (record.phones && record.phones.length > 0) {
      person.contact = record.phones.map((el, i) => {
        if (typeof el === 'string' || el instanceof String) {
          return {
            type: i == 0 ? 'Cell' : 'Home',
            number: el
          };
        }

        return {
          type: el.phone_type === 'Cell' ? 'Cell' : 'Home',
          number: el.number
        };
      });
    }

    //get correct format for address
    if (record.addresses || record.address) {
      const { living, mailing } = this.formatAddresses(
        record.addresses || record.address
      );
      person.livingAddress = living;
      person.mailingAddress =
        Object.keys(mailing).length === 0 ? living : mailing;
    }

    //get correct format for employment details
    if (record.employment_info) {
      person.employer = [
        person.employer,
        this.formatEmployementDetails(record.employment_info)
      ];
    }

    //get correct format for identification details
    if (record.identifications) {
      person.identifications = this.formatIdentificationDetails(
        record.identifications,
        person.livingAddress ? person.livingAddress.country : ''
      );
    }

    let response = { persons: [structuredClone(person)] };
    if (record.accounts) {
      response = {
        ...response,
        accounts: this.formatAccounts(person.coreId, record.accounts)
      };
    }

    return response;
  }

  /**
   * return account object from core record
   * @param {object | array} value
   * @return Object<any>
   */
  static formatAccounts(coreId, value) {
    let accounts = [];
    if (value instanceof Array) {
      value.forEach((el) => {
        if (coreId == el.primary_core_id) {
          accounts.push({
            mainAccountNumber: el.account_number,
            coreId: el.primary_core_id
          });
        }
      });
    } else if (value instanceof Object) {
      if (coreId == value.primary_core_id) {
        accounts = [
          {
            mainAccountNumber: value.account_number,
            coreId: value.primary_core_id
          }
        ];
      }
    }

    return accounts;
  }

  /**
   * return employement_info object from core records
   * @param {object | array} value
   * @return Object<any>
   */
  static formatEmployementDetails(value) {
    let details = {};
    let obj = null;
    let isArray = value instanceof Array;
    let isObject = value instanceof Object;

    if (isArray || (isObject && value)) {
      if (isArray && value.length > 0) {
        obj = value[0];
      }

      details.nameOfEmployer = obj.employer;
      details.isPrimary = true;
      details.employmentStatus = obj.employment_status;
      details.position = obj.position;
      details.dateEmployed = obj.year_of_employment;
      details.telephone = obj.telephone;
      details.email = obj.email;
      details.street = obj.street;
      details.city = obj.street2 || obj.city;
      details.state = (obj.province || '')
        .replace('ST.', 'Saint')
        .replace(/\w\S*/g, (txt) => {
          return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        });
      details.country = obj.country;
    }

    return details;
  }

  /**
   * return living and mailing addresses from core records
   * @param {object | array} value
   * @return Object<{living: Object<any>, mailing: Object<any>}>
   */
  static formatAddresses(value) {
    let living = {};
    let mailing = {};

    const isArray = Array.isArray(value);
    const convert = (obj, val) => {
      obj.city = val.city || val.street3 || val.street2;
      obj.street =
        val.city || val.street3 ? `${val.street} ${val.street2}` : val.street;
      obj.province = (val.province || val.street3 || '')
        .replace('ST.', 'Saint')
        .replace(/\w\S*/g, (txt) => {
          return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        });
      obj.country = val.country;
    };

    //check if api data is array or object and convert accordingly
    if (isArray) {
      value.forEach((el) => {
        if (el.type == 'mailing') convert(mailing, el);
        else convert(living, el);
      });
    } else if (value && value.toString() === '[object Object]') {
      convert(living, value);
    }

    return { living, mailing };
  }

  /**
   * return an array of formatted ids
   * @param {object | array} value
   * @return Array<Object>
   */
  static formatIdentificationDetails(value, defaultCountry) {
    let ids = [];

    const isArray = Array.isArray(value);
    const convert = (val) => {
      return {
        idType: val.id_type || 'N/A',
        country: val.country || defaultCountry || 'N/A',
        number: val.number || 'N/A',
        expiryDate: val.expiry_date || val.ExpiryDate || 'N/A'
      };
    };

    //check if api data is array or object and convert accordingly
    if (isArray) {
      value.forEach((el) => {
        ids.push(convert(el));
      });
    } else if (value && value.toString() === '[object Object]') {
      ids.push(convert(value));
    }

    return ids;
  }
}
